import { ChatMessageBody, ChatMessageType } from "@/types/chat";
import React from "react";
import { NewTaskModel, TASK_STATUS, taskStatusOptions } from "@/types/newWork";
import { TaskResourceBizType, TaskResourcePermissionType, WorkUserInfo } from "@/types/work";
import { getExtName } from "@/utils/chat/index";
import dayjs from "dayjs";
import { nanoid } from "nanoid";
import { openUserSelectionDefaultModal } from "@/utils/modals";
import { FileStatus } from "./CreateTaskDraw";
import DeeplinkEvent, { DeeplinkPathEnum } from "@/components/DeeplinkHandler/DeeplinkEvent";
import config, { ConfigEnum } from "@/config/config";
import { ShareRecipientDto, shareScheduleAttachment } from "@/api/calendar";
import ImService from "@/server/ImService";
import { generateMessageId } from "@jd/jdee.im.sdk/lib/es/utils/utils";
import lodashCompact from "lodash/compact";
import IconFont from "@/components/icon";
import prompt from "@/baseComponents/ModalComponent/prompt";

import { message } from "antd";
import bus from "@/utils/bus";
import {
  ContactShowType,
  SelectGroupType
} from "@jd/focus-desktop-comps/lib/es/UserSelection/types";
import { userSelectionTabSort } from "@/types/common";
import { findIndexExecutorInTask, findSelfInExecutors } from "./utils";
// import { htmlUnescape } from '@/utils/tools';

/**
 * @description 判断任务是否完成或中止
 */
export function judgeTaskFinishOrStop(status: number | null | undefined) {
  if (!status) {
    return false;
  }
  return [TASK_STATUS.FINISHED, TASK_STATUS.ABORT].includes(status);
}
export function judgeTaskFinishAndStop(status: number | null | undefined) {
  if (!status) {
    return [];
  }
  return [TASK_STATUS.FINISHED === status, TASK_STATUS.ABORT === status];
}

/**
 * @description 获取当前任务状态对象
 */
export function getTaskStatus(status: number | null | undefined) {
  const target = taskStatusOptions.find((item) => item.status === status);
  return target;
}

/**
 * 装饰任务详情对象，补全和转换一些字段，
 */
export function decorateTaskDetail(task: NewTaskModel) {
  task.workStartTime = task.startTime ? dayjs(task.startTime) : null;
  task.workStopTime = task.endTime ? dayjs(task.endTime) : null;
  task.remark = task.remark
    ? task.remark
    : {
        id: "",
        remark: ""
      };
  // 提前‘去转义’处理，因为标题有可能会成为群聊天标题(createGroup 接口的入参)——废掉；因为后端先还原转义再转义
  // task.title = htmlUnescape(task.title);
  task.resources = task.resources || [];
  return task;
}

// eslint-disable-next-line complexity
export function decorateQuickCreateTaskResource(resource: any) {
  let fileId = nanoid(20);
  if (resource.type === ChatMessageType.TEMPLATE2) {
    const joyspaceFile =
      resource.data && resource.data[0] ? resource.data[0].sharelink : { category: "", url: "" };
    let pageType = joyspaceFile.pageType || getPageType(joyspaceFile);
    fileId = joyspaceFile.url.split("?")[0].split("/").pop();
    return {
      fileName: joyspaceFile.title || "无标题",
      name: joyspaceFile.title || "无标题",
      size: 0,
      fileSize: 0,
      fileId: fileId,
      bizId: fileId,
      fileType: `joyspace/${pageType}/${joyspaceFile.type}`,
      // fileUrl: `${link}?jdme_router=${encodeURIComponent(r.linkH5)}`,
      url: joyspaceFile.url,
      fileUrl: joyspaceFile.url,
      bizType: TaskResourceBizType.JOYSPACE,
      resourceType: TaskResourcePermissionType.COMMON,
      resourceId: nanoid(),
      percent: 0,
      status: FileStatus.Done
    };
  } else if (
    resource.type === ChatMessageType.JOYSPACEFILE ||
    resource.type === ChatMessageType.JOYSPACEFOLDER
  ) {
    return {
      fileName: resource.fileName || "无标题",
      name: resource.fileName || "无标题",
      size: 0,
      fileSize: 0,
      fileId: resource.fileId,
      bizId: resource.fileId,
      fileType: `joyspace/${resource.pageType}/${resource.type}`,
      // fileUrl: `${link}?jdme_router=${encodeURIComponent(r.linkH5)}`,
      url: resource.webUrl,
      fileUrl: resource.webUrl,
      bizType: TaskResourceBizType.JOYSPACE,
      resourceType: TaskResourcePermissionType.COMMON,
      resourceId: nanoid(),
      percent: 0,
      status: FileStatus.Done
    };
  }
  const isImage = resource.type ? resource.type === "image" : false;
  resource.url = resource.url || resource.fileUrl;
  const [partI, partII] = resource.url?.split("?") || [];
  const extName = getExtName(partI);
  const id = isImage ? `${resource.id}.${extName || "jpg"}` : resource.id;
  return {
    fileName: resource.fileName || resource.name || id,
    fileUrl: resource.url,
    fileId: resource.fileId || fileId,
    name: resource.fileName || resource.name || id,
    fileSize: resource.size || resource.fileSize,
    size: resource.size || resource.fileSize,
    url: resource.url,
    resourceId: nanoid(),
    bizId: resource.fileId || fileId,
    bizType:
      resource.bizType ||
      ((resource.fileType || resource.type || "").startsWith("joyspace")
        ? TaskResourceBizType.JOYSPACE
        : TaskResourceBizType.FILE),
    resourceType: TaskResourcePermissionType.COMMON,
    fileType: resource.fileType || (isImage ? "image/jpeg" : "application/octet-stream"),
    percent: 0,
    status: FileStatus.Done
    // icon: getFileIconByMime(
    //     resource.fileType || lookup(resource.fileName) || 'application/octet-stream'
    // ),
  };
}

function getPageType(file: any) {
  // 为了兼容移动端消息的代码
  // joyspace文档
  if (file.category === "jd_doc") {
    return 1;
  }

  // wps
  const fileExtension = file.title.split(".").pop();
  switch (fileExtension) {
    case "doc":
    case "xls":
      return 6;
    default:
      return 1;
  }
}
export function onShareClick(onTaskDetail: any, userData: any) {
  const { teamUserInfo, userId } = userData;
  const imService = ImService.getInstance();
  const { ddAppId, teamId, realName } = teamUserInfo;
  if (!onTaskDetail) {
    return;
  }
  openUserSelectionDefaultModal(
    {
      title: "分享给朋友",
      tabs: !config[ConfigEnum.EXTERNAL_CONTACTS_ORG]
        ? ["recent", "workLine", "externalContacts", "groupCanBeSel"]
        : ["recent", "org", "workLine", "externalContacts", "groupCanBeSel"],
      tabSort: userSelectionTabSort,
      // tabs: ['recent', 'org', 'group', 'externalContacts'],
      // frozenIds: list.map((item) => item.userId + ':' + item.app + ':' + item.teamId),
      // resultUsers: list.map((item) => ({
      //     id: item.userId + ':' + item.app + ':' + item.teamId,
      //     name: item.realName,
      //     avatar: item.headImg,
      //     checked: true,
      //     userId: item.userId,
      //     teamId: item.teamId,
      //     visible: false,
      // })),
      contactShowType: ContactShowType.sameTenement,
      // contactsFilterFlag: ["crossPlatform", "sameTeam", "crossTeam"],
      selectGroupType: SelectGroupType.innerGroup,
      currentUser: {
        app: ddAppId,
        pin: userId,
        teamId: teamId
      }
    },
    async (data, close) => {
      // 过滤掉已选中的
      const { result } = data.data;
      if (!result.length) {
        return close();
      }
      if (!onTaskDetail) {
        return;
      }
      const addArr: any[] = [];
      const groupArr: any[] = [];
      const originGroupArr: any[] = [];

      result.forEach((r) => {
        if (r.isGroup) {
          originGroupArr.push(r);
          return groupArr.push(r.id);
        }
        if (r.id) {
          const strs = r.id.split(":");
          return addArr.push({
            userId: strs[0],
            teamId: strs[2],
            app: strs[1],
            taskUserRole: 1,
            headImg: r.avatar,
            realName: r.name
          });
        }
      });
      close();
      const { title } = onTaskDetail;
      const sharelink = {
        ext: ``,
        icon:
          "http://storage.360buyimg.com/jd.jme.photo/message_icon_task2x.png?Expires=3757406611&AccessKey=93c0d2d5a6cf315c3d4c52c5f549a9a886b59f76&Signature=pTm0aXa1Azq0eea7K91GNDWDypo%3D",
        // source: 'joyWork',
        category: "joywork",
        title: `来自%s的任务分享`.replace("%s", realName),
        content: title,
        url: DeeplinkEvent.strify({
          appId: ddAppId,
          path: DeeplinkPathEnum.Task_Draw,
          mparam: {
            taskId: onTaskDetail.taskId,
            taskName: onTaskDetail.title,
            from: "mobile",
            priorityType: onTaskDetail.priorityType,
            dueTag: onTaskDetail.dueTag,
            endTime: onTaskDetail.endTime,
            startTime: onTaskDetail.startTime,
            content: onTaskDetail.comment
          }
        })
      };
      const msg: ChatMessageBody = {
        type: ChatMessageType.TEMPLATE2,
        template: {
          nativeId: "share_link"
        },
        code: 0,
        data: [
          {
            sharelink: sharelink,
            type: 0
          }
        ]
      };
      let shareRecipientDtos: ShareRecipientDto[] = [];

      for (let item of addArr) {
        shareRecipientDtos.push({
          recipientType: "1",
          recipientId: item.userId,
          recipientName: item.realName,
          teamId: item.teamId,
          app: item.app
        });
        const session = await imService.createSingleSession({
          app: item.app,
          pin: item.userId,
          teamId: item.teamId
        });
        if (session) {
          const messageId = generateMessageId();
          await imService.sendChatMessage(session.sessionId, messageId, msg);
        }
      }
      originGroupArr.forEach((og) => {
        shareRecipientDtos.push({
          recipientType: "2",
          recipientId: og.id,
          recipientName: og.name,
          app: ddAppId
        });
      });
      groupArr.forEach((a) => {
        const sessionId = a;
        const messageId = generateMessageId();
        imService.sendChatMessage(sessionId, messageId, msg);
      });

      const [_shareRes, shareErr] = await shareScheduleAttachment({
        bizId: onTaskDetail.taskId,
        bizType: 1,
        shareRecipientDtos: shareRecipientDtos
      });
      if (shareErr) {
        console.error("分享日程文档权限报错:", shareErr);
        return message.error(shareErr);
      }
      message.success("分享成功");
    },
    userData
  );
}

export const handleUpdateOwner = (taskDetail: NewTaskModel, userData: any) => {
  const { userId, teamUserInfo } = userData;
  const { ddAppId, teamId } = teamUserInfo;
  // const isGroup = selectedSession?.["isGroup"];
  // const sessionId = selectedSession?.["sessionId"];
  let tabs = ["recent", "org", "workLine", "externalContacts", "group"];
  if (!config[ConfigEnum.EXTERNAL_CONTACTS_ORG]) {
    tabs = ["recent", "workLine", "externalContacts", "group"];
  }
  // if (isGroup) {
  //   tabs.push("business");
  // }
  openUserSelectionDefaultModal(
    {
      title: "添加成员",
      tabs: JSON.parse(JSON.stringify(tabs)),
      tabSort: userSelectionTabSort,
      single: true,
      recommendParam: {
        keyWords: taskDetail.title || "",
        aiType: ["empl"]
      },
      contactShowType: ContactShowType.sameTenement,
      // contactsFilterFlag: ["crossPlatform", "sameTeam", "crossTeam"],
      currentUser: {
        app: ddAppId,
        pin: userId,
        teamId: teamId
      },
      businessTab: {
        tabName: "当前群成员",
        tabParam: {
          businessType: "project",
          businessId: ""
        }
      },

      resultUsers: lodashCompact([taskDetail.owner]).map((item: any) => ({
        id: item.userId + ":" + item.app + ":" + item.teamId,
        name: item.realName,
        avatar: item.headImg,
        userId: item.userId,
        teamId: item.teamId,
        checked: true,
        visible: true
      }))
    },
    (data, close) => {
      // 过滤掉已选中的
      const { result } = data.data;
      if (!result.length) {
        return close();
      }
      const addArr: any[] = [];
      result.forEach((r: any) => {
        if (r.id) {
          const strs = r.id.split(":");
          return addArr.push({
            userId: strs[0],
            teamId: strs[2],
            app: strs[1],
            headImg: r.avatar,
            realName: r.name
          });
        }
        const { origin } = r;
        if (origin.info) {
          addArr.push({
            userId: origin.info.userId,
            teamId: origin.info.teamId,
            app: origin.info.appId || origin.info.app,
            headImg: origin.info.headImg,
            realName: origin.info.realName
          });
        } else {
          addArr.push({
            userId: origin.userId,
            teamId: origin.teamId,
            app: origin.app,
            headImg: origin.headImg,
            realName: origin.realName
          });
        }
      });
      prompt({
        title: `${!taskDetail.owner ? "设置负责人" : "修改负责人"}`,
        icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />,
        onOk: () => {
          console.log("hahahaahah", addArr[0]);
          bus.emit("task:update_owner", { task: taskDetail, owner: addArr[0] });
        },
        content: `是否确认将 %s 设置为该任务负责人？`.replace("%s", addArr[0].realName),
        okText: "确认",
        cancelText: "取消"
      });

      close();
      return addArr[0];
    },
    userData
  );
};
